import * as React from 'react';
import { type ZodError, type ZodIssue, z } from 'zod';

import { zOrderListResponse, zOrderResponse } from '~/api/generated';
import {
  ordersV1OrderListOptions,
  ordersV1OrderListQueryKey,
} from '~/api/generated/@tanstack/react-query.gen';
import type { OrdersV1OrderListData } from '~/api/generated/types.gen';
import { ApiOrderResponseSchema } from '~/types/order';
import { reportOrderSchemaValidationFailure } from '~/utils/reportOrderDataFailure';
import { snakeToCamelCase } from '~/utils/snakeToCamelCase';

import { extractErrorMessage, toast } from '../useToast';

// The list endpoint's exclusion projection omits fields the list table doesn't
// render, and normalize_fields sends them back as null even for orders that have
// data. The API's list item schema (OrderResponse) declares primary_contact and
// shipping_address nullable, so the generated zOrderResponse / zOrderListResponse
// already accept them on the legacy=false path.
//
// ApiOrderResponseSchema is hand-written, so the legacy=true path relaxes the same
// fields here. extend() replaces the named keys; every other field keeps the base
// schema. The detail endpoint always reloads the full order, so these nulls are safe.
const ApiOrderListItemSchema = ApiOrderResponseSchema.extend({
  primaryContact: ApiOrderResponseSchema.shape.primaryContact.nullish(),
  secondaryContact: ApiOrderResponseSchema.shape.secondaryContact.nullish(),
  shippingAddress: ApiOrderResponseSchema.shape.shippingAddress.nullish(),
});

export type OrderListQuery = NonNullable<OrdersV1OrderListData['query']>;
export type OrderListQueryKey = ReturnType<typeof ordersV1OrderListQueryKey>;

const getOrderId = (order: unknown): string | null => {
  if (!order || typeof order !== 'object' || !('id' in order)) return null;

  const { id } = order;
  if (typeof id !== 'string' && typeof id !== 'number') return null;

  return String(id);
};

type OrderValidationMiss = {
  orderId: string | null;
  message: string;
};

function humanizeIssuePath(path: ZodIssue['path']) {
  return path
    .map((part) =>
      String(part)
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/_/g, ' ')
        .toLowerCase()
    )
    .join(' > ');
}

function validationIssueMessage(issue: ZodIssue) {
  const field = humanizeIssuePath(issue.path);
  if (!field) return issue.message;

  if (issue.message.includes('received null')) {
    return `${field} is missing.`;
  }

  return `${field}: ${issue.message}`;
}

function orderValidationMessage(error: ZodError) {
  return error.issues.map(validationIssueMessage).slice(0, 3).join('; ') || error.message;
}

function orderLabel(orderId: string | null) {
  return orderId ? `#${orderId}` : 'Unknown order';
}

function orderLink(orderId: string | null) {
  if (!orderId) return React.createElement('span', null, orderLabel(orderId));

  return React.createElement(
    'a',
    {
      href: `/app/orders/${orderId}`,
      className: 'underline underline-offset-2',
    },
    orderLabel(orderId)
  );
}

function orderListValidationToastDescription(invalidOrders: OrderValidationMiss[]) {
  const groupedOrders = invalidOrders.reduce<Map<string, Array<string | null>>>(
    (acc, { message, orderId }) => {
      acc.set(message, [...(acc.get(message) ?? []), orderId]);
      return acc;
    },
    new Map()
  );

  return React.createElement(
    'div',
    { className: 'space-y-2' },
    React.createElement(
      'p',
      null,
      `${invalidOrders.length} ${invalidOrders.length === 1 ? 'order has' : 'orders have'} invalid data.`
    ),
    Array.from(groupedOrders.entries()).map(([message, orderIds]) =>
      React.createElement(
        'div',
        { key: message, className: 'space-y-1' },
        React.createElement('p', { className: 'font-medium' }, message),
        React.createElement(
          'div',
          { className: 'ml-4 flex max-h-48 flex-col overflow-y-auto' },
          orderIds.map((orderId, index) =>
            React.createElement(
              'div',
              { key: orderId ?? `unknown-order-${index}` },
              orderLink(orderId)
            )
          )
        )
      )
    )
  );
}

function reportOrderListValidationFailures(orders: unknown[], orderSchema: z.ZodType) {
  const misses = orders
    .map((order) => ({
      parsed: orderSchema.safeParse(order),
      orderId: getOrderId(order),
    }))
    .filter((result) => !result.parsed.success);

  misses.forEach(({ parsed, orderId }) => {
    if (parsed.success) return;

    reportOrderSchemaValidationFailure({
      error: parsed.error,
      orderId,
    });
  });

  return misses.reduce<OrderValidationMiss[]>((acc, { parsed, orderId }) => {
    if (parsed.success) return acc;

    acc.push({
      orderId,
      message: orderValidationMessage(parsed.error),
    });

    return acc;
  }, []);
}

export function orderListQuery(query: OrderListQuery = {}, legacy: boolean = false) {
  return ordersV1OrderListOptions({
    query: query,
    responseValidator: async (data: any) => {
      let parseError: ZodError | undefined;
      let invalidOrders: OrderValidationMiss[] = [];

      if (legacy) {
        const camelPayload = snakeToCamelCase(data.payload);
        const parsedLegacyList = z.array(ApiOrderListItemSchema).safeParse(camelPayload);
        parseError = parsedLegacyList.error;

        if (Array.isArray(camelPayload)) {
          invalidOrders = reportOrderListValidationFailures(camelPayload, ApiOrderListItemSchema);
        }
      } else {
        parseError = zOrderListResponse.safeParse(data).error;

        if (Array.isArray(data.payload)) {
          invalidOrders = reportOrderListValidationFailures(data.payload, zOrderResponse);
        }
      }

      if (parseError) {
        toast({
          title: 'Order Data',
          description:
            invalidOrders.length > 0
              ? orderListValidationToastDescription(invalidOrders)
              : extractErrorMessage(parseError),
          variant: 'destructive',
        });

        if (invalidOrders.length === 0) {
          reportOrderSchemaValidationFailure({
            error: parseError,
            orderId: null,
          });
        }
      }
    },
  });
}
